// Smooth motion between polls.
//
// Feeds arrive every 10–20 seconds. Drawn raw, every vehicle teleports on each
// poll, which reads as broken even though the data is fine. This eases each
// vehicle from where it was to where it now is over the expected gap, so the
// map moves continuously.
//
// PURE MODULE: no window, no document, no MapLibre. Per docs/MOBILE.md this is
// the kind of logic that must run unchanged in a WebView or React Native, so
// keep browser globals out of it.

import type { Vehicle } from './types.ts';

interface Track {
  from: { lat: number; lon: number; heading: number | null };
  to: { lat: number; lon: number; heading: number | null };
  startedAt: number;
  durationMs: number;
  vehicle: Vehicle;
}

/** Shortest signed angular difference in degrees, in (-180, 180]. */
function angleDelta(a: number, b: number): number {
  return ((((b - a) % 360) + 540) % 360) - 180;
}

export class Interpolator {
  private tracks = new Map<string, Track>();

  /**
   * Replaces the current set. Vehicles absent from `next` are dropped; ones
   * already known ease from their present drawn position rather than their last
   * reported one, so a late poll does not cause a visible jump backwards.
   */
  update(next: readonly Vehicle[], now: number, durationMs: number): void {
    const seen = new Set<string>();

    for (const v of next) {
      seen.add(v.id);
      const existing = this.tracks.get(v.id);
      const from = existing
        ? this.positionAt(existing, now)
        : { lat: v.lat, lon: v.lon, heading: v.heading };

      this.tracks.set(v.id, {
        from,
        to: { lat: v.lat, lon: v.lon, heading: v.heading },
        startedAt: now,
        durationMs,
        vehicle: v,
      });
    }

    for (const id of this.tracks.keys()) {
      if (!seen.has(id)) this.tracks.delete(id);
    }
  }

  private positionAt(track: Track, now: number): { lat: number; lon: number; heading: number | null } {
    const elapsed = now - track.startedAt;
    const t = track.durationMs <= 0 ? 1 : Math.min(1, Math.max(0, elapsed / track.durationMs));
    // Ease-out: vehicles decelerate into their reported position rather than
    // stopping dead, which hides the discontinuity at the end of each segment.
    const e = 1 - (1 - t) * (1 - t);

    const lat = track.from.lat + (track.to.lat - track.from.lat) * e;
    const lon = track.from.lon + (track.to.lon - track.from.lon) * e;

    let heading = track.to.heading;
    if (track.from.heading !== null && track.to.heading !== null) {
      // Rotate the short way round, so 350° → 10° does not spin backwards.
      heading = track.from.heading + angleDelta(track.from.heading, track.to.heading) * e;
    }
    return { lat, lon, heading };
  }

  /** Current drawn state of every tracked vehicle. */
  sample(now: number): Vehicle[] {
    const out: Vehicle[] = [];
    for (const track of this.tracks.values()) {
      const p = this.positionAt(track, now);
      out.push({ ...track.vehicle, lat: p.lat, lon: p.lon, heading: p.heading });
    }
    return out;
  }

  get size(): number {
    return this.tracks.size;
  }
}
